"use client";

import { Button } from "@/components/ui/button";
import { CurrencyInput } from "@/components/ui/currency-input";
import { FilteredSelect } from "@/components/ui/filtered-select";
import { Phone } from "lucide-react";

import { Label } from "@/components/ui/label";
import { CustomPopover } from "@/components/ui/popover-custom";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Stepper } from "@/components/ui/stepper";
import { useLocationContext } from "@/contexts/location-context";
import { useQuoteContext } from "@/contexts/quote-context";
import { buscarCep, jsonVeiculos } from "@/services/flowQuote";
import { VeiculoJson } from "@/types/Veiculo";
import { zodResolver } from "@hookform/resolvers/zod";
import { ArrowRight, Loader2 } from "lucide-react";
import { useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";

import {
  trackCotacaoStep1Submit,
  trackCotacaoValidacaoCepErro,
  trackCotacaoValidacaoCepOk,
} from "@/analytics/tracking";
import { parseBRLCurrencyToNumber } from "@/analytics/format";
import { FilteredSelectLocation } from "@/components/ui/filtered-select-location-2";

const stepOneSchema = z.object({
  originLocation: z
    .string({ message: "Informe o local de origem" })
    .min(1, { message: "Informe o local de origem" }),
  destinationLocation: z
    .string({ message: "Informe o local de destino" })
    .min(1, { message: "Informe o local de destino" }),
  brand: z
    .string({ message: "Selecione a Marca/Modelo do veículo" })
    .min(1, { message: "Selecione uma marca/modelo" }),
  value: z
    .string({ message: "Informe o valor do veículo" })
    .min(1, { message: "Informe o valor do veículo" }),
  isWorking: z
    .string({ message: "Informe se o veículo está funcionando" })
    .min(1, { message: "Informe se o veículo está funcionando" }),
  isArmored: z.string().min(1, "Informe se o veículo é blindado"),
});

type StepOneFormValues = z.infer<typeof stepOneSchema>;

type Option = { label: string; value: string };

export default function StepOne({ onNext }: { onNext: () => void }) {
  const {
    updateTransportData,
    updateVehicleCotation,
    transportData,
    vehicleCotation,
  } = useQuoteContext();

  const { locations } = useLocationContext();

  const [vehicleOptions, setVehicleOptions] = useState<Option[]>([]);
  const [loading, setLoading] = useState(false);

  const {
    control,
    handleSubmit,
    formState: { errors },
    setError,
    clearErrors,
  } = useForm<StepOneFormValues>({
    resolver: zodResolver(stepOneSchema),
    defaultValues: {
      ...transportData,
      ...vehicleCotation,
      isArmored: "false",
      isWorking: "true",
    },
  });

  useEffect(() => {
    async function fetchVehicles() {
      const data = await jsonVeiculos();
      setVehicleOptions(
        (data || []).map((v: VeiculoJson) => ({
          label: v?.vmd_descricao,
          value: v?.vmd_codigo,
        })),
      );
    }
    fetchVehicles();
  }, []);

  const validateLocation = async (
    input: string,
    field: "originLocation" | "destinationLocation",
  ) => {
    clearErrors(field);
    const isInList = locations.some((loc: Option) => loc.value === input);
    if (isInList) {
      trackCotacaoValidacaoCepOk(
        field === "originLocation" ? "origin" : "destination",
        input,
        input,
      );
      return input;
    }

    const cepRegex = /^\d{5}-?\d{3}$/;
    if (!cepRegex.test(input)) {
      setError(field, {
        type: "manual",
        message: "Digite um CEP válido ou selecione um município da lista.",
      });
      trackCotacaoValidacaoCepErro(
        field === "originLocation" ? "origin" : "destination",
        input,
        "cep_invalido",
      );
      return null;
    }

    try {
      const result = await buscarCep(input.replace("-", ""));
      const munCodigo = result?.[1]?.mun_codigo as string | undefined;

      if (munCodigo) {
        if (field === "originLocation") {
          updateTransportData({ cepOriginLocation: input });
        } else {
          updateTransportData({ cepDestinationLocation: input });
        }

        trackCotacaoValidacaoCepOk(
          field === "originLocation" ? "origin" : "destination",
          input,
          munCodigo,
        );
      }

      return munCodigo || null;
    } catch {
      setError(field, {
        type: "manual",
        message: "Não foi possível localizar esse CEP.",
      });
      trackCotacaoValidacaoCepErro(
        field === "originLocation" ? "origin" : "destination",
        input,
        "cep_nao_encontrado",
      );
      return null;
    }
  };

  const onSubmit = async (data: StepOneFormValues) => {
    setLoading(true);

    const origin = await validateLocation(
      data.originLocation,
      "originLocation",
    );
    const destination = await validateLocation(
      data.destinationLocation,
      "destinationLocation",
    );
    const brandValid = vehicleOptions.find((v) => v.value === data.brand);

    if (!origin || !destination || !brandValid) {
      if (!brandValid) {
        setError("brand", { message: "Selecione uma marca válida da lista" });
      }
      setLoading(false);
      return;
    }

    updateTransportData({
      originLocation: origin,
      destinationLocation: destination,
    });
    updateVehicleCotation({
      brand: data.brand,
      value: data.value,
      isWorking: data.isWorking,
      isArmored: data.isArmored,
    });
    const nameColeta = locations.find((o) => o.value === origin);
    const nameEntrega = locations.find((o) => o.value === destination);

    trackCotacaoStep1Submit({
      origin_location_input: nameColeta.label,
      origin_location_code: origin,
      destination_location_input: nameEntrega.label,
      destination_location_code: destination,
      vehicle_brand_code: data.brand,
      vehicle_brand_label: brandValid?.label,
      vehicle_value_brl: parseBRLCurrencyToNumber(data.value),
      is_working: data.isWorking === "true",
      is_armored: data.isArmored === "true",
    });

    setLoading(false);
    onNext();
  };

  return (
    <div className="flex flex-col h-full">
      <div className="p-6 md:p-8 space-y-6 flex-1 overflow-y-auto justify-start pb-12 md:pb-8">
        <div>
          <h1 className="hidden md:block text-2xl md:text-3xl font-semibold text-n-primary text-center">
            Vamos calcular o seu transporte?
          </h1>
          <div className="md:hidden flex flex-col items-center justify-start">
            <h1 className="text-lg font-bold text-n-text-neutral text-center">
              Calcule seu transporte agora
            </h1>
            <h2 className="text-lg text-n-text">
              ou ligue para{" "}
              <a
                href="tel:08008780708"
                className="font-bold underline underline-offset-2 hover:text-n-primary text-n-text-green transition-colors cursor-pointer"
              >
                0800 878 0708
              </a>
            </h2>
          </div>
        </div>

        <Stepper currentStep={1} totalSteps={2} />

        <form
          onSubmit={handleSubmit(onSubmit)}
          className="flex flex-col md:gap-4 gap-2"
        >
          <div>
            <Controller
              name="originLocation"
              control={control}
              render={({ field }) => (
                <FilteredSelectLocation
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Município de origem"
                  options={locations as Option[]}
                  onEnter={(raw) => validateLocation(raw, "originLocation")}
                  onSelect={() => clearErrors("originLocation")}
                  textEmpty="Usar Cep:"
                />
              )}
            />
            {errors.originLocation && (
              <p className="text-n-error text-xs mt-1 ml-2">
                {errors.originLocation.message}
              </p>
            )}
          </div>

          <div>
            <Controller
              name="destinationLocation"
              control={control}
              render={({ field }) => (
                <FilteredSelectLocation
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Município de destino"
                  options={locations as Option[]}
                  onEnter={(raw) =>
                    validateLocation(raw, "destinationLocation")
                  }
                  onSelect={() => clearErrors("destinationLocation")}
                  textEmpty="Usar Cep:"
                />
              )}
            />
            {errors.destinationLocation && (
              <p className="text-n-error text-xs mt-1 ml-2">
                {errors.destinationLocation.message}
              </p>
            )}
          </div>

          <div>
            <Controller
              name="brand"
              control={control}
              render={({ field }) => (
                <FilteredSelect
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Marca e modelo do veículo"
                  options={vehicleOptions}
                />
              )}
            />
            {errors.brand && (
              <p className="text-n-error text-xs mt-1 ml-2">
                {errors.brand.message}
              </p>
            )}
          </div>

          <div>
            <Controller
              name="value"
              control={control}
              render={({ field }) => (
                <CurrencyInput
                  value={field.value}
                  onChange={field.onChange}
                  placeholder="Valor do veículo (para seguro)"
                />
              )}
            />
            {errors.value && (
              <p className="text-n-error text-xs mt-1 ml-2">
                {errors.value.message}
              </p>
            )}
          </div>

          <div className="flex justify-between items-center py-3 border-b mx-2 border-n-neutral-lighter">
            <Label className="md:text-base text-sm font-normal text-n-text-neutral min-w-[50%]">
              O veículo está funcionando?
            </Label>
            <Controller
              name="isWorking"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="working-yes" />
                    <Label htmlFor="working-yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="working-no" />
                    <Label htmlFor="working-no">Não</Label>
                  </div>
                </RadioGroup>
              )}
            />
            <CustomPopover
              iconSize={20}
              title="Veículo está funcionando?"
              description="Se o veículo não estiver funcionando, pode haver custos adicionais para o transporte. Informe corretamente para evitar surpresas."
            />
          </div>

          <div className="flex justify-between items-center py-3 mx-2 border-b border-n-neutral-lighter">
            <Label className="md:text-base text-xs font-normal text-n-text-neutral min-w-[50%]">
              O veículo é blindado?
            </Label>
            <Controller
              name="isArmored"
              control={control}
              render={({ field }) => (
                <RadioGroup
                  value={field.value}
                  onValueChange={field.onChange}
                  className="flex gap-4"
                >
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="true" id="armored-yes" />
                    <Label htmlFor="armored-yes">Sim</Label>
                  </div>
                  <div className="flex items-center space-x-2">
                    <RadioGroupItem value="false" id="armored-no" />
                    <Label htmlFor="armored-no">Não</Label>
                  </div>
                </RadioGroup>
              )}
            />
            <CustomPopover
              iconSize={20}
              title="Veículo é blindado?"
              description="Se o veículo for blindado, pode haver custos adicionais para o transporte. Informe corretamente para evitar surpresas."
            />
          </div>
        </form>
      </div>

      <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-white border-t border-gray-200 p-4 flex gap-2 z-50">
        <Button
          type="button"
          variant="green"
          className="flex-1 flex items-center justify-center gap-2 bg-n-beige"
          onClick={() => window.open("tel:08008780708", "_self")}
        >
          <Phone className="w-4 h-4" />
          Ligue Agora
        </Button>
        <Button
          type="button"
          className="flex-1 flex items-center justify-center gap-2"
          disabled={loading}
          onClick={handleSubmit(onSubmit)}
        >
          {loading ? (
            <>
              Carregando... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
            </>
          ) : (
            <>
              Prosseguir <ArrowRight className="ml-2 h-4 w-4" />
            </>
          )}
        </Button>
      </div>

      <div className="hidden lg:block p-6 pt-0">
        <div className="flex justify-end mt-3">
          <Button
            type="button"
            disabled={loading}
            onClick={handleSubmit(onSubmit)}
          >
            {loading ? (
              <>
                Carregando... <Loader2 className="ml-2 h-4 w-4 animate-spin" />
              </>
            ) : (
              <>
                Prosseguir <ArrowRight className="ml-2 " />
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
}
